import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Search, Calendar, User, ArrowRight, Tag } from "lucide-react";

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await axios.get(window.API_URL + "/blogs");
        setBlogs(res.data);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  // Helper to strip HTML tags from content preview
  const stripHtml = (html) => {
    return html.replace(/<[^>]*>/g, "");
  };

  // Get all unique tags from blogs
  const allTags = React.useMemo(() => {
    const tagsSet = new Set();
    blogs.forEach((blog) => {
      if (Array.isArray(blog.tags)) {
        blog.tags.forEach((tag) => {
          if (tag) tagsSet.add(tag.toLowerCase().trim());
        });
      }
    });
    return Array.from(tagsSet);
  }, [blogs]);

  // Filtered blogs based on search and selected tag
  const filteredBlogs = React.useMemo(() => {
    return blogs.filter((blog) => {
      const matchesSearch =
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stripHtml(blog.content).toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesTag =
        !selectedTag ||
        (Array.isArray(blog.tags) &&
          blog.tags.some((t) => t.toLowerCase().trim() === selectedTag.toLowerCase().trim()));

      return matchesSearch && matchesTag;
    });
  }, [blogs, searchTerm, selectedTag]);

  // The latest post is featured
  const featuredBlog = filteredBlogs[0];
  const remainingBlogs = filteredBlogs.slice(1);

  return (
    <div className="bg-gray-50 min-h-screen font-sans">
      {/* Hero Header */}
      <section className="relative overflow-hidden bg-gradient-to-r from-gray-900 via-[#1f1610] to-black py-20 text-white">
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(#f06600_1px,transparent_1px)] [background-size:24px_24px]"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <span className="px-4 py-1.5 rounded-full bg-[#f06600]/10 border border-[#f06600]/30 text-[#f06600] text-xs font-bold uppercase tracking-wider">
            Discover Our Insights
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold mt-4 tracking-tight leading-tight">
            Beta <span className="text-[#f06600]">Agency</span> Blog
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto mt-4 text-base md:text-lg">
            Stay up to date with the latest industry news, technology trends, design frameworks, and community stories.
          </p>
        </div>
      </section>

      {/* Main Search & Filters Panel */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-6 justify-between items-center mb-12">
          {/* Search bar */}
          <div className="relative w-full lg:w-96 group">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-gray-400 group-focus-within:text-[#f06600] transition-colors">
              <Search className="w-5 h-5" />
            </span>
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl outline-none focus:border-[#f06600] focus:ring-1 focus:ring-[#f06600]/30 shadow-sm transition"
            />
          </div>

          {/* Tags Filter */}
          <div className="w-full lg:w-auto flex flex-wrap gap-2 items-center justify-start lg:justify-end">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-2 flex items-center gap-1.5">
              <Tag className="w-4 h-4" /> Filter by:
            </span>
            <button
              onClick={() => setSelectedTag("")}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                !selectedTag
                  ? "bg-[#f06600] text-white shadow-md shadow-[#f06600]/25"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              All Topics
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-4 py-2 rounded-xl text-xs font-bold capitalize transition-all ${
                  selectedTag === tag
                    ? "bg-[#f06600] text-white shadow-md shadow-[#f06600]/25"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-12 h-12 border-4 border-[#f06600]/20 border-t-[#f06600] rounded-full animate-spin"></div>
            <p className="text-gray-500 mt-4 font-medium animate-pulse">Loading articles...</p>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="text-center py-20 bg-white border border-gray-100 rounded-3xl shadow-sm">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-1">No articles found</h3>
            <p className="text-gray-500 max-w-sm mx-auto text-sm">
              We couldn't find any match for "{searchTerm || selectedTag}". Try checking your spelling or selecting another topic.
            </p>
          </div>
        ) : (
          <>
            {/* Featured Post (Only if no search filter active or just regular list first item) */}
            {featuredBlog && !searchTerm && !selectedTag && (
              <div className="mb-16">
                <div className="group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 grid grid-cols-1 lg:grid-cols-2">
                  <div className="relative h-64 sm:h-96 lg:h-full min-h-[320px] bg-gray-100 overflow-hidden">
                    {featuredBlog.images && featuredBlog.images.length > 0 ? (
                      <img
                        src={`${window.API_URL}/uploads/${featuredBlog.images[0]}`}
                        alt={featuredBlog.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Search className="w-16 h-16" />
                      </div>
                    )}
                    <span className="absolute top-6 left-6 px-3.5 py-1.5 bg-[#f06600] text-white text-xs font-extrabold rounded-full uppercase tracking-wider shadow">
                      Latest Article
                    </span>
                  </div>
                  <div className="p-8 md:p-12 flex flex-col justify-center">
                    <div className="flex items-center text-xs font-semibold text-gray-400 gap-4 mb-4">
                      <span className="flex items-center gap-1.5">
                        <User className="w-4 h-4" /> {featuredBlog.author}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" /> {new Date(featuredBlog.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    </div>

                    <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4 tracking-tight leading-snug group-hover:text-[#f06600] transition-colors duration-300">
                      <Link to={`/blog/${featuredBlog.slug}`}>{featuredBlog.title}</Link>
                    </h2>

                    <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-6 line-clamp-4">
                      {stripHtml(featuredBlog.content)}
                    </p>

                    {featuredBlog.tags && featuredBlog.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-8">
                        {featuredBlog.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg capitalize"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div>
                      <Link
                        to={`/blog/${featuredBlog.slug}`}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white hover:bg-[#f06600] font-bold text-sm rounded-xl shadow-md transition-all group-hover:translate-x-1"
                      >
                        Read Article <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Blogs Cards Grid */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-8 border-b pb-4 border-gray-100">
                {searchTerm || selectedTag ? "Search Results" : "All Articles"}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {(searchTerm || selectedTag ? filteredBlogs : remainingBlogs).map((blog) => (
                  <article
                    key={blog._id}
                    className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:border-gray-200 transition-all duration-300 flex flex-col hover:-translate-y-1.5"
                  >
                    <div className="relative h-56 bg-gray-100 shrink-0 overflow-hidden">
                      {blog.images && blog.images.length > 0 ? (
                        <img
                          src={`${window.API_URL}/uploads/${blog.images[0]}`}
                          alt={blog.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Search className="w-12 h-12" />
                        </div>
                      )}
                      {blog.tags && blog.tags.length > 0 && (
                        <span className="absolute top-4 left-4 px-2.5 py-1 bg-black/60 backdrop-blur-md text-white text-[10px] font-bold rounded-lg uppercase tracking-wider capitalize">
                          {blog.tags[0]}
                        </span>
                      )}
                    </div>

                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center text-[11px] font-semibold text-gray-400 gap-3 mb-3">
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" /> {blog.author}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" /> {new Date(blog.createdAt).toLocaleDateString()}
                        </span>
                      </div>

                      <h4 className="font-extrabold text-lg text-gray-900 mb-2 leading-snug line-clamp-2 group-hover:text-[#f06600] transition-colors duration-300">
                        <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                      </h4>

                      <p className="text-gray-500 text-xs md:text-sm leading-relaxed mb-4 line-clamp-3">
                        {stripHtml(blog.content)}
                      </p>

                      <div className="mt-auto pt-4 border-t border-gray-50 flex items-center justify-between">
                        <span className="text-[11px] font-medium text-[#f06600] uppercase tracking-wider">
                          {blog.layoutStyle === "carousel"
                            ? "Gallery Cover"
                            : blog.layoutStyle === "grid"
                            ? "Interactive Grid"
                            : "Standard Post"}
                        </span>
                        <Link
                          to={`/blog/${blog.slug}`}
                          className="inline-flex items-center gap-1 text-xs font-bold text-gray-900 group-hover:text-[#f06600] transition-colors"
                        >
                          Read More <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BlogList;
